import {
  AssignmentInd,
  BusinessCenter,
  LocationOn,
  OpenInNew,
  Person,
} from '@mui/icons-material';
import { Box, Chip, Grid, Paper, Typography } from '@mui/material';
import { IconButton } from '@mui/material';
import { getPrincipalInfo } from 'lib/api/sgr';
import InfoCard from 'lib/designSystem/InfoCard';
import { formatAddress } from 'lib/functions/utils';
import { parseRutWithDots } from 'lib/functions/utils';
import React from 'react';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

interface Props {
  id: number;
}

const PrincipalInfo: React.FC<Props> = ({ id }) => {
  const dispatch = useDispatch();
  const { data: principal, isPending } = getPrincipalInfo(dispatch, id);
  const principalIdentifier = principal?.business?.identifier;
  const profileUrl = useMemo(() => {
    const formattedRut = parseRutWithDots(principalIdentifier);
    if (!formattedRut) return null;
    return `https://comprador.mercadopublico.cl/ficha/informacion-general/${formattedRut}`;
  }, [principalIdentifier]);

  const showSkeleton = isPending && !principal;
  const unitCodeValue = principal?.unit
    ? `${principal.unit.name ?? 'N/A'} (${principal.unit.unitCode ?? 'N/A'})`
    : 'N/A';

  return (
    <Paper sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1rem',
            lineHeight: 1.1,
          }}
        >
          Información del Mandante
        </Typography>
        <Chip
          label="Detalles"
          size="small"
          color="primary"
          variant="outlined"
          sx={{ height: '18px', fontSize: '0.65rem' }}
        />
        {profileUrl && (
          <IconButton
            component="a"
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            aria-label="Abrir mandante"
            sx={{ p: 0.25 }}
          >
            <OpenInNew sx={{ fontSize: '0.9rem' }} />
          </IconButton>
        )}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <InfoCard
            icon={<Person />}
            label="Razón Social"
            value={principal?.business.name ?? 'N/A'}
            color="blue"
            pending={showSkeleton}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <InfoCard
            icon={<AssignmentInd />}
            label="RUT"
            value={principal?.business.identifier ?? 'N/A'}
            color="blue"
            pending={showSkeleton}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <InfoCard
            icon={<LocationOn />}
            label="Dirección"
            value={formatAddress(principal?.address) ?? 'N/A'}
            color="orange"
            pending={showSkeleton}
          />
        </Grid>

        {(showSkeleton || principal?.unit) && (
          <Grid item xs={12} md={6}>
            <InfoCard
              icon={<BusinessCenter />}
              label="Unidad de Negocio"
              value={unitCodeValue}
              color="purple"
              pending={showSkeleton}
            />
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default PrincipalInfo;
